'use client';
import { useState } from 'react';
import { useRef } from 'react';

import Button from '@/components/ui/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleRight } from '@fortawesome/free-solid-svg-icons';
import { useTranslations } from '@/utils/translations';

const CONSENT_KEY = 'cookie_consent_v2';

interface ConsentState {
  analytics: boolean;
  advertising: boolean;
  personalization: boolean;
}

function pushConsentUpdate(consent: ConsentState) {
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer || [];
  function gtag(...args: any[]) {
    window.dataLayer.push(args);
  }
  (gtag as any)('consent', 'update', {
    ad_storage: consent.advertising ? 'granted' : 'denied',
    ad_user_data: consent.advertising ? 'granted' : 'denied',
    ad_personalization: consent.personalization ? 'granted' : 'denied',
    analytics_storage: consent.analytics ? 'granted' : 'denied',
  });
  window.dataLayer.push({
    event: 'consent_update',
    consent_analytics: consent.analytics ? 'granted' : 'denied',
    consent_advertising: consent.advertising ? 'granted' : 'denied',
    consent_personalization: consent.personalization ? 'granted' : 'denied',
  });
}

interface FooterProps {
  className?: string;
}

type ModalType = 'terms' | 'impressum' | 'cookies' | 'kontakt' | null;

const Footer = ({ className }: FooterProps) => {
  const { t } = useTranslations('cz');
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [contactMessage, setContactMessage] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [isMessageSent, setIsMessageSent] = useState(false);

  // Cookie consent state
  const [cookieConsent, setCookieConsent] = useState<ConsentState>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem(CONSENT_KEY);
      if (saved) {
        try { return JSON.parse(saved); } catch { /* ignore */ }
      }
    }
    return { analytics: false, advertising: false, personalization: false };
  });
  const [consentSaved, setConsentSaved] = useState(false);

  const openModal = (modal: ModalType) => {
    setActiveModal(modal);
    if (modal === 'cookies') {
      setConsentSaved(false);
      // Load current saved consent when opening
      if (typeof window !== 'undefined') {
        const saved = localStorage.getItem(CONSENT_KEY);
        if (saved) {
          try { setCookieConsent(JSON.parse(saved)); } catch { /* ignore */ }
        }
      }
    }
  };

  const closeModal = () => {
    setActiveModal(null);
  };

  // Auto-expand textarea based on scrollHeight
  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>): void => {
    setContactMessage(e.target.value);

    // Reset height to auto to get the correct scrollHeight
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  };

  const handleSendMessage = (): void => {
    if (contactMessage.trim()) {
      setIsMessageSent(true);
      // Here you can add logic to send the message to backend
      console.log('Message sent:', contactMessage);
      console.log('Email:', contactEmail);
      // Reset after 3 seconds and close modal
      setTimeout(() => {
        setContactMessage('');
        setContactEmail('');
        setIsMessageSent(false);
        if (textareaRef.current) {
          textareaRef.current.style.height = 'auto';
        }
        closeModal();
      }, 3000);
    }
  };

  const handleSaveConsentPreferences = () => {
    localStorage.setItem(CONSENT_KEY, JSON.stringify(cookieConsent));
    pushConsentUpdate(cookieConsent);
    setConsentSaved(true);
    setTimeout(() => setConsentSaved(false), 2500);
  };

  const handleResetConsentDefaults = () => {
    const defaults: ConsentState = { analytics: false, advertising: false, personalization: false };
    setCookieConsent(defaults);
    localStorage.setItem(CONSENT_KEY, JSON.stringify(defaults));
    pushConsentUpdate(defaults);
    setConsentSaved(true);
    setTimeout(() => setConsentSaved(false), 2500);
  };

  return (
    <footer className="w-full max-w-[1580px]  mx-auto px-[10px] sm:px-[15px] md:px-[20px] mt-[20px] sm:mt-[30px] md:mt-[40px]">
        <div className="w-full justify-center bg-footer-background opacity-100 rounded-t-[25px] px-[2px] sm:px-[3px] md:px-[4px] py-[12px] sm:py-[13px] md:py-[14px]">
          <div className="w-full flex justify-center items-center">
            <div className="flex gap-[9px] sm:gap-[13px] md:gap-[18px] justify-center items-center">
              <button
              onClick={() => openModal('terms')}
              className="text-[10px] sm:text-[10px] md:text-[12px] font-inter font-medium leading-[11px] sm:leading-[13px] md:leading-[15px] text-center text-[#FFFFFF] hover:underline cursor-pointer">
                {t('footer.links.terms_of_use')}
              </button>
              <button
              onClick={() => openModal('impressum')}
              className="text-[10px] sm:text-[10px] md:text-[12px] font-inter font-medium leading-[11px] sm:leading-[13px] md:leading-[15px] text-center text-[#FFFFFF] hover:underline cursor-pointer">
                {t('footer.links.impressum')}
              </button>
              <button
              onClick={() => openModal('kontakt')}
              className="text-[10px] sm:text-[10px] md:text-[12px] font-inter font-medium leading-[11px] sm:leading-[13px] md:leading-[15px] text-center text-[#FFFFFF] hover:underline cursor-pointer">
                {t('footer.links.contact')}
              </button>
              <button
              onClick={() => openModal('cookies')}
              className="text-[10px] sm:text-[10px] md:text-[12px] font-inter font-medium leading-[11px] sm:leading-[13px] md:leading-[15px] text-center text-[#FFFFFF] hover:underline cursor-pointer">
                {t('footer.links.cookies')}
              </button>
            </div>
          </div>
        </div>

        {/* Terms Modal */}
        {activeModal === 'terms' && (
      <div className="fixed inset-0 flex items-end justify-center z-50 animate-slideUp">
            <div className="absolute inset-0 bg-black bg-opacity-30" onClick={closeModal}></div>
            <div className="relative flex flex-col justify-start items-center w-full max-w-[600px] bg-[#e3e3e3] rounded-t-[16px] p-[25px] mb-0 max-h-[80vh] overflow-y-auto">
              <div className="flex flex-col justify-start items-center w-full gap-[20px]">
                <div className="relative w-full h-[56px] flex justify-center items-center">
                  <h1 className="text-[30px] font-inter font-bold leading-[39px] text-center text-[#989898]">
                    {t('footer.terms_modal.title')}
                  </h1>
                </div>

                <div className="flex flex-col justify-start items-start w-full gap-[16px]">
                  <p className="font-inter font-medium text-[13px] sm:text-[14px] md:text-[15px] lg:text-[16px] leading-[20px] sm:leading-[22px] md:leading-[22px] lg:leading-[22px] text-left text-[#555151]">
                    {t('footer.terms_modal.paragraph_1')}
                  </p>
                  <p className="font-inter font-medium text-[13px] sm:text-[14px] md:text-[15px] lg:text-[16px] leading-[20px] sm:leading-[22px] md:leading-[22px] lg:leading-[22px] text-left text-[#555151]">
                    {t('footer.terms_modal.paragraph_2')}
                  </p>
                  <p className="font-inter font-medium text-[13px] sm:text-[14px] md:text-[15px] lg:text-[16px] leading-[20px] sm:leading-[22px] md:leading-[22px] lg:leading-[22px] text-left text-[#555151]">
                    {t('footer.terms_modal.paragraph_3')}
                  </p>
                  <p className="font-inter font-medium text-[13px] sm:text-[14px] md:text-[15px] lg:text-[16px] leading-[20px] sm:leading-[22px] md:leading-[22px] lg:leading-[22px] text-left text-[#555151]">
                    {t('footer.terms_modal.paragraph_4')}
                  </p>
                </div>

                <div className="flex items-center justify-center w-full mt-[20px]">
                  <Button
                text={t('footer.terms_modal.close_button')}
                onClick={closeModal}
                className="bg-[#77C0BB] text-[#444444] font-inter font-medium text-[16px] leading-[30px] rounded-[24px] px-[34px] py-[8px] flex items-center gap-[8px]">
                    <span>{t('footer.terms_modal.close_button')}</span>
                  </Button>
                </div>
              </div>
            </div>
          </div>
      )}

        {/* Impressum Modal */}
        {activeModal === 'impressum' && (
      <div className="fixed inset-0 flex items-end justify-center z-50 animate-slideUp">
            <div className="absolute inset-0 bg-black bg-opacity-30" onClick={closeModal}></div>
            <div className="relative flex flex-col justify-start items-center w-full max-w-[600px] bg-[#e3e3e3] rounded-t-[16px] p-[25px] mb-0 max-h-[80vh] overflow-y-auto">
              <div className="flex flex-col justify-start items-center w-full gap-[20px]">
                <div className="relative w-full h-[56px] flex justify-center items-center">
                  <h1 className="text-[30px] font-inter font-bold leading-[39px] text-center text-[#989898]">
                    {t('footer.impressum_modal.title')}
                  </h1>
                </div>

                <div className="flex flex-col justify-start items-center w-full gap-[16px]">
                  <p className="font-inter font-semi-bold text-[14px] sm:text-[15px] md:text-[16px] lg:text-[17px] leading-[20px] sm:leading-[22px] md:leading-[22px] lg:leading-[22px] text-center text-[#555151]">
                    {t('footer.impressum_modal.operator_label')}
                  </p>
                  <p className="font-inter font-medium text-[13px] sm:text-[14px] md:text-[15px] lg:text-[16px] leading-[20px] sm:leading-[22px] md:leading-[22px] lg:leading-[22px] text-center text-[#555151]">
                    {t('footer.impressum_modal.company_name')}<br />
                    {t('footer.impressum_modal.address_line_1')}<br />
                    {t('footer.impressum_modal.address_line_2')}
                  </p>
                  <p className="font-inter font-medium text-[13px] sm:text-[14px] md:text-[15px] lg:text-[16px] leading-[20px] sm:leading-[22px] md:leading-[22px] lg:leading-[22px] text-center text-[#555151]">
                    {t('footer.impressum_modal.company_id')}<br />
                  </p>
                  <p className="font-inter font-medium text-[13px] sm:text-[14px] md:text-[15px] lg:text-[16px] leading-[20px] sm:leading-[22px] md:leading-[22px] lg:leading-[22px] text-center text-[#555151]">
                    {t('footer.impressum_modal.company_description')}
                  </p>
                </div>

                <div className="flex items-center justify-center w-full mt-[20px]">
                  <Button
                text={t('footer.impressum_modal.close_button')}
                onClick={closeModal}
                className="bg-[#77C0BB] text-[#444444] font-inter font-medium text-[16px] leading-[30px] rounded-[24px] px-[34px] py-[8px] flex items-center gap-[8px]">
                    <span>{t('footer.impressum_modal.close_button')}</span>
                  </Button>
                </div>
              </div>
            </div>
          </div>
      )}

        {/* Cookies Modal */}
        {activeModal === 'cookies' && (
      <div className="fixed inset-0 flex items-end justify-center z-50 animate-slideUp">
            <div className="absolute inset-0 bg-black bg-opacity-30" onClick={closeModal}></div>
            <div className="relative flex flex-col justify-start items-center w-full max-w-[600px] bg-[#e3e3e3] rounded-t-[16px] p-[25px] mb-0 max-h-[80vh] overflow-y-auto">
              <div className="flex flex-col justify-start items-center w-full gap-[20px]">

                {/* Granular Consent Preferences */}
                <div className="w-full bg-white rounded-[12px] p-[16px] flex flex-col gap-[14px] border border-[#d0d0d0]">
                  <p className="font-inter font-semibold text-[14px] text-[#555151]">
                    {t('footer.cookies_modal.preferences_heading')}
                  </p>

                  {/* Necessary - always on */}
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <p className="font-inter font-semibold text-[13px] text-[#444444]">{t('footer.cookies_modal.necessary_title')}</p>
                      <p className="font-inter text-[12px] text-[#888888] mt-0.5">{t('footer.cookies_modal.necessary_description')}</p>
                    </div>
                    <div className="flex-shrink-0 mt-0.5">
                      <div className="w-11 h-6 bg-[#77C0BB] rounded-full flex items-center justify-end px-1 cursor-not-allowed opacity-60">
                        <div className="w-4 h-4 bg-white rounded-full" />
                      </div>
                    </div>
                  </div>

                  {/* Analytics */}
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <p className="font-inter font-semibold text-[13px] text-[#444444]">{t('footer.cookies_modal.analytics_title')}</p>
                      <p className="font-inter text-[12px] text-[#888888] mt-0.5">{t('footer.cookies_modal.analytics_description')}</p>
                    </div>
                    <button
                      role="switch"
                      aria-checked={cookieConsent.analytics}
                      onClick={() => setCookieConsent(prev => ({ ...prev, analytics: !prev.analytics }))}
                      className={`flex-shrink-0 mt-0.5 w-11 h-6 rounded-full flex items-center px-1 transition-colors duration-200 ${
                        cookieConsent.analytics ? 'bg-[#77C0BB] justify-end' : 'bg-gray-300 justify-start'
                      }`}
                    >
                      <div className="w-4 h-4 bg-white rounded-full shadow" />
                    </button>
                  </div>

                  {/* Advertising */}
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <p className="font-inter font-semibold text-[13px] text-[#444444]">{t('footer.cookies_modal.advertising_title')}</p>
                      <p className="font-inter text-[12px] text-[#888888] mt-0.5">{t('footer.cookies_modal.advertising_description')}</p>
                    </div>
                    <button
                      role="switch"
                      aria-checked={cookieConsent.advertising}
                      onClick={() => setCookieConsent(prev => ({ ...prev, advertising: !prev.advertising }))}
                      className={`flex-shrink-0 mt-0.5 w-11 h-6 rounded-full flex items-center px-1 transition-colors duration-200 ${
                        cookieConsent.advertising ? 'bg-[#77C0BB] justify-end' : 'bg-gray-300 justify-start'
                      }`}
                    >
                      <div className="w-4 h-4 bg-white rounded-full shadow" />
                    </button>
                  </div>

                  {/* Personalization */}
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <p className="font-inter font-semibold text-[13px] text-[#444444]">{t('footer.cookies_modal.personalization_title')}</p>
                      <p className="font-inter text-[12px] text-[#888888] mt-0.5">{t('footer.cookies_modal.personalization_description')}</p>
                    </div>
                    <button
                      role="switch"
                      aria-checked={cookieConsent.personalization}
                      onClick={() => setCookieConsent(prev => ({ ...prev, personalization: !prev.personalization }))}
                      className={`flex-shrink-0 mt-0.5 w-11 h-6 rounded-full flex items-center px-1 transition-colors duration-200 ${
                        cookieConsent.personalization ? 'bg-[#77C0BB] justify-end' : 'bg-gray-300 justify-start'
                      }`}
                    >
                      <div className="w-4 h-4 bg-white rounded-full shadow" />
                    </button>
                  </div>

                  {/* Save / Reset buttons */}
                  <div className="flex flex-col sm:flex-row gap-[10px] mt-[4px]">
                    <button
                      onClick={handleSaveConsentPreferences}
                      className="flex-1 px-4 py-2.5 bg-[#77C0BB] hover:bg-[#5aada8] text-[#1a1a2e] font-inter font-semibold text-[13px] rounded-[10px] transition-colors duration-200">
                      {t('footer.cookies_modal.save_preferences')}
                    </button>
                    <button
                      onClick={handleResetConsentDefaults}
                      className="flex-1 px-4 py-2.5 bg-transparent border border-[#aaaaaa] hover:border-[#777777] text-[#777777] hover:text-[#444444] font-inter font-medium text-[13px] rounded-[10px] transition-colors duration-200">
                      {t('footer.cookies_modal.reset_defaults')}
                    </button>
                  </div>

                  {/* Saved confirmation */}
                  {consentSaved && (
                    <p className="text-center text-[12px] font-inter text-[#77C0BB] font-medium">
                      {t('footer.cookies_modal.preferences_saved')}
                    </p>
                  )}
                </div>

                <div className="flex items-center justify-center w-full mt-[4px]">
                  <Button
                text={t('footer.cookies_modal.close_button')}
                onClick={closeModal}
                className="bg-[#77C0BB] text-[#444444] font-inter font-medium text-[16px] leading-[30px] rounded-[24px] px-[34px] py-[8px] flex items-center gap-[8px]">
                    <span>{t('footer.cookies_modal.close_button')}</span>
                  </Button>
                </div>
              </div>
            </div>
          </div>
      )}

        {/* Kontakt Modal */}
        {activeModal === 'kontakt' && (
          <div className="fixed inset-0 flex items-end justify-center z-50 animate-slideUp">
            <div className="absolute inset-0 bg-black bg-opacity-30" onClick={closeModal}></div>
            <div className="relative flex flex-col justify-start items-center w-full max-w-[600px] bg-[#e3e3e3] rounded-t-[16px] p-[25px] mb-0 max-h-[80vh] overflow-y-auto">
              <div className="flex flex-col justify-start items-center w-full gap-[20px] px-2.5">
                <div className="relative w-full h-[56px] flex justify-center items-center">
                  <h1 className="text-[30px] font-inter font-bold leading-[39px] text-center text-[#989898]">
                    {t('footer.contact_modal.title')}
                  </h1>
                </div>

                <div className="flex flex-col justify-start items-center w-full gap-[16px]">
                  <p className="font-inter font-semi-bold text-[14px] sm:text-[15px] md:text-[16px] lg:text-[17px] leading-[20px] sm:leading-[22px] md:leading-[22px] lg:leading-[22px] text-center text-[#555151]">
                    {t('footer.contact_modal.description')}
                  </p>
                </div>

                {/* Email and Question Input Fields */}
                <div className="w-full flex flex-col gap-4 mt-4">
                  {!isMessageSent ? (
                    <>
                      {/* Email Input Field */}
                      <div className="relative w-full">
                        <input
                          type="email"
                          value={contactEmail}
                          onChange={(e) => setContactEmail(e.target.value)}
                          placeholder={t('footer.contact_modal.email_placeholder')}
                          className="w-full px-4 py-3 rounded-[12px] border-2 border-[#77C0BB] bg-white text-[#555151] font-inter text-[14px] sm:text-[16px] focus:outline-none focus:border-[#b71b90] transition-colors"
                          style={{ minHeight: '48px' }}
                        />
                      </div>

                      {/* Question Input Field */}
                      <div className="relative w-full">
                        <textarea
                          ref={textareaRef}
                          value={contactMessage}
                          onChange={handleTextareaChange}
                          rows={1}
                          placeholder={t('footer.contact_modal.message_placeholder')}
                          className="w-full px-4 py-3 pr-[52px] rounded-[12px] border-2 border-[#77C0BB] bg-white text-[#555151] font-inter text-[14px] sm:text-[16px] focus:outline-none focus:border-[#b71b90] transition-colors resize-none overflow-hidden"
                          style={{ minHeight: '48px' }} />

                        <button
                          onClick={handleSendMessage}
                          disabled={!contactMessage.trim()}
                          className="absolute bottom-2 right-2 text-[#77C0BB] hover:text-[#b71b90] transition-colors disabled:opacity-40 disabled:cursor-not-allowed text-[32px]"
                          aria-label={t('footer.contact_modal.send_message_aria')}>
                          <FontAwesomeIcon icon={faCircleRight} />
                        </button>
                      </div>
                    </>
                  ) : (
                    <div className="w-full px-4 py-3 rounded-[12px] bg-[#77C0BB] bg-opacity-20 text-[#555151] font-inter text-[14px] sm:text-[16px] text-center">
                      {t('footer.contact_modal.success_message')}
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-center w-full mt-[20px]">
                  <Button
                    text={t('footer.contact_modal.close_button')}
                    onClick={closeModal}
                    className="bg-[#77C0BB] text-[#444444] font-inter font-medium text-[16px] leading-[30px] rounded-[24px] px-[34px] py-[8px] flex items-center gap-[8px]">
                    <span>{t('footer.contact_modal.close_button')}</span>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </footer>);

};

export default Footer;